import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { trackFormEvent } from "@/lib/track";

// Static-friendly: the browser posts leads DIRECTLY to the n8n webhook.
// No own backend required. Overridable via REACT_APP_N8N_WEBHOOK_URL at build time.
const WEBHOOK_URL =
  process.env.REACT_APP_N8N_WEBHOOK_URL ||
  "https://ck-partners.app.n8n.cloud/webhook/lead-intake";

const empty = { name: "", email: "", phone: "", company: "", message: "", website: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  const update = (k) => (e) => {
    if (!started) {
      setStarted(true);
      trackFormEvent("form_start");
    }
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Bitte Name, E-Mail und eine kurze Nachricht ausfüllen.");
      return;
    }
    // Honeypot: real users never fill this hidden field, bots often do.
    // Silently drop instead of hitting the webhook.
    if (form.website.trim()) {
      setDone(true);
      setForm(empty);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          message: form.message,
          source: "kurpfalz-automation-website",
          submitted_at: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(`Webhook responded ${res.status}`);
      trackFormEvent("form_submit");
      setDone(true);
      setForm(empty);
      toast.success("Danke! Ihre Anfrage ist bei uns eingegangen.");
    } catch (err) {
      console.error(err);
      toast.error("Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "mt-2 h-12 text-base bg-white border-slate-300 focus-visible:ring-[#16273F] rounded-lg";

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-[#0E1B2E] text-white py-24 sm:py-32 scroll-mt-24 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-32 -right-24 h-96 w-96 rounded-full bg-[#9A8420]/10 blur-3xl" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: pitch */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#9A8420]" />
              <span className="eyebrow text-[#C9A94A]">Kontakt</span>
            </div>
            <h2 className="mt-6 font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
              Lassen Sie uns kurz sprechen.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-md">
              Schreiben Sie uns, was bei Ihnen gerade Zeit kostet. Wir melden uns
              persönlich und schauen gemeinsam, ob und wie wir helfen können.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href="mailto:ck-partners@gmx.net"
                data-testid="contact-email-link"
                className="group flex items-center gap-4 text-slate-200 hover:text-white transition-colors"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-[#C9A94A] group-hover:border-[#9A8420] transition-colors">
                  <Mail className="w-5 h-5" />
                </span>
                ck-partners@gmx.net
              </a>
              <a
                href="https://wa.me/4915233560557"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="group flex items-center gap-4 text-slate-200 hover:text-white transition-colors"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-[#C9A94A] group-hover:border-[#9A8420] transition-colors">
                  <MessageCircle className="w-5 h-5" />
                </span>
                WhatsApp: +49 152 33560557
              </a>
              <div className="flex items-center gap-4 text-slate-200">
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-[#C9A94A]">
                  <MapPin className="w-5 h-5" />
                </span>
                Gesamte Rhein-Neckar-Region — Mannheim, Heidelberg, Ludwigshafen, Weinheim, Viernheim, Schwetzingen und Speyer
              </div>
            </div>
          </motion.div>

          {/* Right: form card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="bg-white text-[#16273F] rounded-2xl p-6 sm:p-9 shadow-2xl">
              {done ? (
                <div data-testid="contact-success" className="text-center py-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F7F3E4] text-[#9A8420]">
                    <CheckCircle2 className="w-9 h-9" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-bold text-2xl">
                    Vielen Dank für Ihre Anfrage
                  </h3>
                  <p className="mt-2 text-slate-600">
                    Wir haben Ihre Nachricht erhalten und melden uns zeitnah bei Ihnen.
                  </p>
                  <button
                    data-testid="contact-reset"
                    onClick={() => setDone(false)}
                    className="mt-6 text-sm font-semibold text-[#9A8420] hover:text-[#7d6b1a] transition-colors"
                  >
                    Weitere Anfrage senden
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={update("website")}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="font-semibold">Name *</Label>
                      <Input id="name" data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Ihr Name" className={inputCls} />
                    </div>
                    <div>
                      <Label htmlFor="company" className="font-semibold">Betrieb</Label>
                      <Input id="company" data-testid="contact-input-company" value={form.company} onChange={update("company")} placeholder="Name Ihres Betriebs" className={inputCls} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="font-semibold">E-Mail *</Label>
                      <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={update("email")} placeholder="name@betrieb.de" className={inputCls} />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="font-semibold">Telefon</Label>
                      <Input id="phone" data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="Für einen Rückruf" className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message" className="font-semibold">Ihre Nachricht *</Label>
                    <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={update("message")} placeholder="Was kostet Sie gerade am meisten Zeit?" rows={5} className="mt-2 text-base bg-white border-slate-300 focus-visible:ring-[#16273F] rounded-lg resize-none" />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="group w-full inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full bg-[#16273F] hover:bg-[#0E1B2E] disabled:opacity-70 text-white text-base font-semibold transition-colors"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Wird gesendet…</>
                    ) : (
                      <>Kostenloses Erstgespräch anfragen
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="text-center text-xs text-slate-500">
                    Ihre Angaben werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
